// page lookup for the horizon bar's ask-box typeahead: the second section,
// under the recent-questions one.
//
// the page list comes from site_map_pages rather than the nav registry. the
// registry only knows what the RAIL links, while the site map is the canonical
// "everything this account can reach" list and already carries descriptions
// and per-page accessibility, which is exactly what a search needs.
//
// building that list walks every section and its report cards, which is far
// too much work to redo on each keystroke, so it is built once per
// user/tenant/view-as and filtered from the cache after that.

import { buildSiteMapSections, flattenSiteMapPages } from '@/utils/site_map_pages'

let cacheKey = null
let cachePages = []

function keyFor(user, viewAs) {
  return [
    (user && user.tenant) || '',
    (user && user.role) || '',
    (user && user.username) || '',
    viewAs || '',
  ].join('|')
}

// only pages that are actually reachable: the site map deliberately lists
// locked ones so it can explain what they need, but a nav shortcut to a page
// that will bounce is a dead end, not a suggestion.
function allPages(user, viewAs) {
  const key = keyFor(user, viewAs)
  if (key === cacheKey) return cachePages
  try {
    cachePages = flattenSiteMapPages(buildSiteMapSections(user, viewAs))
      .filter(p => p && p.accessible !== false && (p.route || p.path))
  } catch (_) {
    // a malformed profile must not take the whole typeahead down; the recent
    // questions section still works without this one
    cachePages = []
  }
  cacheKey = key
  return cachePages
}

// ranked substring match. a page whose NAME starts with what was typed is the
// strongest signal, then a name containing it, then its section, then its
// description -- so typing "driver" leads with the pages actually called
// Driver-something rather than everything that mentions drivers.
export function searchPages(user, viewAs, query, limit = 5) {
  const q = (query || '').trim().toLowerCase()
  if (!q) return []
  const scored = []
  allPages(user, viewAs).forEach(p => {
    const name = (p.name || '').toLowerCase()
    const section = (p.sectionTitle || '').toLowerCase()
    const desc = (p.description || '').toLowerCase()
    let score = -1
    if (name.indexOf(q) === 0) score = 0
    else if (name.indexOf(q) !== -1) score = 1
    else if (section.indexOf(q) !== -1) score = 2
    else if (desc.indexOf(q) !== -1) score = 3
    if (score >= 0) scored.push({ page: p, score })
  })
  scored.sort((a, b) => a.score - b.score || (a.page.name || '').localeCompare(b.page.name || ''))
  return scored.slice(0, limit).map(s => s.page)
}

// the down-arrow fallback: with nothing typed and no recent questions there is
// still an answer to "what is in here", so the list falls back to the site map
// itself -- the same sections the site map page shows, each with every page
// under it, in the same order. no limit and no per-section trim: this is the
// map, and a map that hides places is worse than none. the caller heads each
// group off the pages' own sectionTitle.
// a copy, not the cache itself: the list is handed to a component that may
// slice or sort it, and the cache has to survive that.
export function siteMapPages(user, viewAs) {
  return allPages(user, viewAs).slice()
}

// exported for tests: the cache is module-level, so a test that changes the
// profile between cases has to be able to drop it
export function _resetPageCache() {
  cacheKey = null
  cachePages = []
}
